/**
 * Rates how demanding a design is to actually sew, from the stats the creator's
 * export left behind (see stitchStats on the public metadata document).
 *
 * The rating leans on color count as much as stitch count, because the work a
 * person does is mostly thread changes: every color means stopping the machine,
 * rethreading, and trimming jumps. A 30,000-stitch design in two colors is a
 * long wait; the same size in nine colors is an afternoon of babysitting.
 *
 * Necessarily approximate. These numbers come from the creator's export at their
 * hoop size, and whoever opens the design re-exports at their own — which moves
 * the stitch count. Treat it as a browsing hint, not a spec.
 */

// Each axis scores 0-3, so the total lands in 0-6.
const scoreColors = (colors) => {
  if (colors <= 2) return 0;
  if (colors <= 5) return 1;
  if (colors <= 8) return 2;
  return 3;
};

const scoreStitches = (stitches) => {
  if (stitches < 8000) return 0;
  if (stitches < 20000) return 1;
  if (stitches < 40000) return 2;
  return 3;
};

// Backgrounds are near-opaque rather than a light tint: the badge sits directly
// on the artwork, which is usually white, and a translucent wash left the text
// competing with whatever was behind it.
const LEVELS = [
  { label: "Easy", color: "#15803d", background: "rgba(240,253,244,0.94)" },
  { label: "Intermediate", color: "#b45309", background: "rgba(255,251,235,0.94)" },
  { label: "Advanced", color: "#b91c1c", background: "rgba(254,242,242,0.94)" },
];

/**
 * @param {{num_stitches?: number|null, num_colors?: number|null}} stitchStats
 * @returns {{label: string, color: string, background: string, detail: string}|null}
 *   null when the design was published without export stats, in which case the
 *   caller should show nothing rather than guess.
 */
export const getStitchDifficulty = (stitchStats) => {
  const stitches = Number(stitchStats?.num_stitches) || 0;
  const colors = Number(stitchStats?.num_colors) || 0;
  if (!stitches && !colors) return null;

  const total = scoreColors(colors) + scoreStitches(stitches);
  const level = total <= 1 ? LEVELS[0] : total <= 3 ? LEVELS[1] : LEVELS[2];

  // Spelled out so the badge can justify itself on hover instead of asking the
  // reader to trust a bare label.
  const parts = [];
  if (colors) parts.push(`${colors} thread ${colors === 1 ? "color" : "colors"} to change`);
  if (stitches) parts.push(`${stitches.toLocaleString()} stitches at the creator's size`);

  return { ...level, detail: parts.join(", ") };
};
